import { useState, useRef, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CREDIT_COSTS } from "@vms/tubeforge-shared";
/** (owner's screenshot, 2026-10-03: the form said "costs 1 credit" for an engine video that reserves 105) — the SAME
 *  figure the backend reserves (engine-credits.service ENGINE_VIDEO_COST: script + render). */
const ENGINE_VIDEO_COST = CREDIT_COSTS.SCRIPT_GENERATION + CREDIT_COSTS.VIDEO_RENDER;
import { useQuery, useMutation } from "@tanstack/react-query";
import { presetsApi } from "@/api/presets.ts";
import { videosApi } from "@/api/videos.ts";
import { assetsApi, type Asset } from "@/api/assets.ts";
import { voicesApi, type Voice } from "@/api/voices.ts";
import { playablePreviewUrl } from "@/lib/voice-preview.ts";
import { PageContainer, TfCard, Btn, Icon, TfSpinner } from "@/components/v2/primitives.tsx";
import type { ApiError } from "@/api/client.ts";
import type { ContentProfile } from "@vms/tubeforge-shared";
import { useTenant } from "@/stores/tenantStore.tsx";
import { DryRunToggle } from "@/components/engine/DryRun.tsx";
import { dryRunField } from "@/lib/dry-run.ts";
import { cleanSavedProfile, hasProfileInput } from "@/lib/content-profile-form.ts";

// ── Local storage key for persisting the profile across sessions ──
const PROFILE_KEY = "tf_content_profile";

function loadSavedProfile(): Partial<ContentProfile> {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? cleanSavedProfile(JSON.parse(raw)) : {};   // (audit 10-14) the old form's invented values dropped
  } catch {  // catch-reason: a saved form that isn't JSON is absent (a fresh form)
    return {};
  }
}

function saveProfile(profile: ContentProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch { /* quota exceeded — ignore */ }
}

// ── Constants ──

const VIDEO_GOALS = [
  { value: "grow_audience", label: "Grow audience", desc: "Maximize views and subscribers", icon: "📈" },
  { value: "sell_product", label: "Sell product", desc: "Drive purchases or link clicks", icon: "💰" },
  { value: "build_authority", label: "Build authority", desc: "Establish expertise and trust", icon: "🏆" },
  { value: "entertain", label: "Entertain", desc: "Maximize watch time and shares", icon: "🎭" },
  { value: "educate", label: "Educate", desc: "Teach something valuable", icon: "📚" },
] as const;

const PLATFORMS = [
  { value: "youtube", label: "YouTube" },
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
  { value: "pinterest", label: "Pinterest" },
  { value: "general", label: "General" },
] as const;

const HOOK_STYLES = [
  { value: "any", label: "Let AI decide" },
  { value: "question", label: "Question" },
  { value: "bold_claim", label: "Bold claim" },
  { value: "story", label: "Story" },
  { value: "controversy", label: "Controversy" },
  { value: "statistic", label: "Statistic" },
] as const;

/** Next drop §1: the three looks the customer can choose (contracts VisualStyle). */
const LOOKS: { value: "photoreal" | "stylized" | "cartoon"; label: string; hint: string }[] = [
  { value: "photoreal", label: "Realistic", hint: "Real-looking people, places and light" },
  { value: "stylized", label: "Stylized", hint: "Glossy, slightly cartoonish — clearly not real" },
  { value: "cartoon", label: "Cartoon", hint: "Fully illustrated, like an animation" },
];

export function NewVideoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const saved = loadSavedProfile();

  // Step 1: preset selection
  const [presetId, setPresetId] = useState(searchParams.get("preset") ?? "");
  // next drop §1: the look — null = the type's own default (pre-selected); a choice is sent on the order
  const [visualStyle, setVisualStyle] = useState<"photoreal" | "stylized" | "cartoon" | null>(null);
  // next drop §1: inside Realistic — like a phone video (the default register) · polished · cinematic
  const [realismRegister, setRealismRegister] = useState<"phone" | "polished" | "cinematic">("phone");

  // Step 2: content profile (persisted across sessions)
  const [channelNiche, setChannelNiche] = useState(saved.channelNiche ?? "");
  const [targetAudience, setTargetAudience] = useState(saved.targetAudience ?? "");
  const [callToAction, setCallToAction] = useState(saved.callToAction ?? "");
  const [platform, setPlatform] = useState<ContentProfile["platform"]>(saved.platform ?? "youtube");
  const [videoGoal, setVideoGoal] = useState<ContentProfile["videoGoal"]>(saved.videoGoal ?? "grow_audience");
  const [hookStyle, setHookStyle] = useState<ContentProfile["hookStyle"]>(saved.hookStyle ?? "any");
  const [productName, setProductName] = useState(saved.productName ?? "");
  const [differentiator, setDifferentiator] = useState(saved.differentiator ?? "");

  // Dynamic lists
  const [sellingPoints, setSellingPoints] = useState<string[]>(saved.sellingPoints ?? []);
  const [painPoints, setPainPoints] = useState<string[]>(saved.audiencePainPoints ?? []);
  const [newSP, setNewSP] = useState("");
  const [newPP, setNewPP] = useState("");

  // Step 3: guidance (prefilled from onboarding topic when arriving via ?topic=)
  const [userGuidance, setUserGuidance] = useState(searchParams.get("topic") ?? searchParams.get("guidance") ?? "");

  // Asset selection
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  const { data: assetsData } = useQuery({
    queryKey: ["assets"],
    queryFn: () => assetsApi.list(),
  });
  const userAssets: Asset[] = (assetsData?.assets ?? []).filter(a => a.mimeType.startsWith("image/"));

  // Strategy selection
  const [strategyId, setStrategyId] = useState("");
  // Session 4 K2: on an 'engine' tenant a video is an order built from a saved strategy and a one-line goal.
  const [goal, setGoal] = useState("");
  const engine = useTenant().tenant?.videoPipeline === "engine";
  const [dryRun, setDryRun] = useState(false);   // (tf3-35b) an admin's dry run (the toggle shows to admins only)
  const { data: strategiesData } = useQuery({
    queryKey: ["strategies"],
    queryFn: () => videosApi.listStrategies(),
  });
  const savedStrategies = strategiesData?.strategies ?? [];

  // Voice selection
  const [voiceProvider] = useState<"elevenlabs">("elevenlabs");
  const [voiceId, setVoiceId] = useState("");
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  // (drop 114b) a preview that no longer loads: the voice is shown without one (never our copy instead)
  const [deadPreviews, setDeadPreviews] = useState<ReadonlySet<string>>(new Set());
  const previewOf = (v: Voice) => (deadPreviews.has(v.id) ? null : playablePreviewUrl(v.previewUrl));
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: voicesData, isLoading: voicesLoading } = useQuery({
    queryKey: ["voices"],
    queryFn: () => voicesApi.list(),
  });
  const voices: Voice[] = voicesData?.voices ?? [];

  const playPreview = (voice: Voice) => {
    const url = previewOf(voice);
    if (!url) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingVoiceId === voice.id) {
      setPlayingVoiceId(null);
      return;
    }
    const audio = new Audio(url);
    audio.onended = () => setPlayingVoiceId(null);
    audio.onerror = () => { setPlayingVoiceId(null); setDeadPreviews((s) => new Set(s).add(voice.id)); };
    // (audit D117) a play cut short by switching voices (its AbortError) must not clear the NEW voice's playing state
    audio.play().catch(() => { if (audioRef.current === audio) setPlayingVoiceId(null); });
    audioRef.current = audio;
    setPlayingVoiceId(voice.id);
  };

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(
    Boolean(productName || differentiator || sellingPoints.length || painPoints.length),
  );

  const { data: presetsData, isLoading: presetsLoading } = useQuery({
    queryKey: ["presets", strategyId],
    queryFn: () => presetsApi.list(strategyId || undefined),
  });

  const createVideo = useMutation({
    mutationFn: () => {
      // (audit 10-14) a product's own fields alone are a profile too
      const hasProfile = hasProfileInput({ channelNiche, targetAudience, callToAction, productName, differentiator, sellingPoints, painPoints });
      const profile: ContentProfile | undefined = hasProfile ? {
        // (TF3-6, T1-5) only what was typed: the engine maps the profile into the video, so an invented value would read
        //  as stated (a blank stays blank; the backend sends it as null)
        channelNiche: channelNiche.trim(),
        targetAudience: targetAudience.trim(),
        callToAction: callToAction.trim(),
        platform,
        videoGoal,
        hookStyle,
        productName: productName || undefined,
        differentiator: differentiator || undefined,
        sellingPoints: sellingPoints.length > 0 ? sellingPoints : undefined,
        audiencePainPoints: painPoints.length > 0 ? painPoints : undefined,
      } : undefined;
      if (hasProfile && profile) saveProfile(profile);
      return videosApi.create({
        presetId,
        userGuidance: userGuidance.trim() || undefined,
        contentProfile: profile,
        assetIds: selectedAssets.size > 0 ? Array.from(selectedAssets) : undefined,
        strategyId: strategyId || undefined,
        ...(engine && visualStyle ? { visualStyle } : {}),
        ...(engine && (visualStyle ?? (selectedPreset as { defaultVisualStyle?: string | null } | undefined)?.defaultVisualStyle ?? "photoreal") === "photoreal"
          && realismRegister !== "phone" ? { realismRegister } : {}),
        goal: goal.trim() || undefined,
        voiceProvider: voiceId ? voiceProvider : undefined,
        voiceId: voiceId || undefined,
        ...(engine ? dryRunField(dryRun) : {}),
      });
    },
    onSuccess: (data) => { navigate(`/videos/${data.video.id}/script`); },   // engine videos review the same route
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!presetId) return;
    createVideo.mutate();
  };

  const error = createVideo.error as ApiError | null;
  const selectedPreset = presetsData?.presets.find(p => p.id === presetId);
  const isProductGoal = videoGoal === "sell_product";
  // Require a preset that actually exists — guards against a stale/invalid
  // ?preset= (e.g. an onboarding niche id that isn't a real preset).
  // (B1, the engine/agency boundary 2026-10-04: a video is a type + a topic — no business. A saved strategy is an
  //  optional CHANNEL strategy; what the engine needs is the one line saying what the video is about.)
  const hasTopic = goal.trim().length >= 3 || (userGuidance ?? "").trim().length >= 3 || !!strategyId;
  const canSubmit = !!selectedPreset && (!engine || hasTopic);
  const blockedWhy = engine && !hasTopic ? "Say what the video is about (one line, below)." : null;

  return (
    <PageContainer title="New video" subtitle="Tell us about your content — the AI directors will handle the rest." maxWidth={860}>
      {presetsLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><TfSpinner size={28} /></div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* ── Strategy — REQUIRED on the engine (it supplies the business the video is built from); optional on legacy.
               On the engine it is always shown, and with no saved strategy it says so and links to create one — before,
               it was hidden when the list was empty, so the order failed STRATEGY_REQUIRED with nothing to choose. ── */}
          {savedStrategies.length > 0 && (
            <TfCard padding={24}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--tf-fg)" }}>
                    {engine ? "Channel strategy" : "Strategy"}{engine && <span className="tf-muted" style={{ fontSize: 11, marginLeft: 6 }}>optional</span>}
                  </div>
                  <div className="tf-muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {engine ? "Your channel's niche and purpose — used to steer the video, never required" : "Use a saved strategy to inform this video's content direction"}
                  </div>
                </div>
                {strategyId && (
                  <Btn variant="ghost" size="sm" onClick={() => setStrategyId("")}>Clear</Btn>
                )}
              </div>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {savedStrategies.map((s: any) => (
                  <button key={s.id} type="button" onClick={() => setStrategyId(s.id)} style={{
                    padding: "10px 14px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                    border: strategyId === s.id ? "2px solid var(--tf-accent)" : "1px solid var(--tf-rule)",
                    background: strategyId === s.id ? "var(--tf-accent-soft)" : "var(--tf-surface)",
                    fontFamily: "inherit", color: "var(--tf-fg)", transition: "all .15s",
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{s.name}</div>
                    <div className="tf-muted" style={{ fontSize: 10, marginTop: 2 }}>
                      {s.channelNiche || "General"} · {new Date(s.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </TfCard>
          )}

          {/* ── Section 1: Preset ── */}
          <TfCard padding={24}>
            <SectionLabel number={1} title="Channel topic" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginTop: 12 }}>
              {presetsData?.presets.map(p => {
                // Session 7 N1: the backend sends a customer only what can run; an ADMIN also gets the rest, greyed
                // here with the reason (they cannot be picked — the backend would refuse them anyway).
                const off = (p as { available?: boolean }).available === false;
                const why = (p as { unavailableReason?: string | null }).unavailableReason ?? undefined;
                return (
                <button key={p.id} type="button" disabled={off} title={off ? why : undefined} onClick={() => { setPresetId(p.id); setVisualStyle(null); }} style={{
                  padding: "12px 14px", textAlign: "left",
                  background: presetId === p.id ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                  border: "1px solid " + (presetId === p.id ? "var(--tf-accent)" : "var(--tf-rule)"),
                  borderRadius: 8, cursor: off ? "not-allowed" : "pointer", fontFamily: "inherit", color: "var(--tf-fg)",
                  opacity: off ? 0.45 : 1,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.displayName}
                    {(presetsData?.recommended ?? []).includes(p.id) && <span className="tf-muted" style={{ fontSize: 11, fontWeight: 500, marginLeft: 6 }}>· Recommended</span>}</div>
                  <div className="tf-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    {off ? `Admin only — ${why ?? "not available"}` : p.format === "shorts" ? "Shorts · 9:16" : "Long-form · 16:9"}
                  </div>
                </button>
                );
              })}
            </div>
            {selectedPreset && (
              <div style={{ padding: 12, background: "var(--tf-surface)", borderRadius: 8, border: "1px solid var(--tf-rule)", fontSize: 12, color: "var(--tf-fg-muted)", marginTop: 10 }}>
                {selectedPreset.description}
              </div>
            )}
          </TfCard>

          {/* ── Section 2: Video goal + Platform ── */}
          <TfCard padding={24}>
            <SectionLabel number={2} title="Video goal" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8, marginTop: 12 }}>
              {VIDEO_GOALS.map(g => (
                <button key={g.value} type="button" onClick={() => setVideoGoal(g.value as ContentProfile["videoGoal"])} style={{
                  padding: "12px 12px", textAlign: "left",
                  background: videoGoal === g.value ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                  border: "1px solid " + (videoGoal === g.value ? "var(--tf-accent)" : "var(--tf-rule)"),
                  borderRadius: 8, cursor: "pointer", fontFamily: "inherit", color: "var(--tf-fg)",
                }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{g.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{g.label}</div>
                  <div className="tf-muted" style={{ fontSize: 10, marginTop: 2 }}>{g.desc}</div>
                </button>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
              <div>
                <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Platform</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {PLATFORMS.map(p => (
                    <button key={p.value} type="button" onClick={() => setPlatform(p.value as ContentProfile["platform"])} style={{
                      padding: "6px 14px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
                      border: "1px solid " + (platform === p.value ? "var(--tf-accent)" : "var(--tf-rule)"),
                      background: platform === p.value ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                      color: platform === p.value ? "var(--tf-accent)" : "var(--tf-fg)",
                      fontSize: 12, fontWeight: 500,
                    }}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {engine && (() => {
                // next drop §1: Realistic / Stylized / Cartoon, the type's default pre-selected
                const typeDefault = ((selectedPreset as { defaultVisualStyle?: string | null } | undefined)?.defaultVisualStyle ?? "photoreal") as "photoreal" | "stylized" | "cartoon";
                const current = visualStyle ?? typeDefault;
                return (
                  <div>
                    <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Look</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {LOOKS.map((l) => (
                        <button key={l.value} type="button" onClick={() => setVisualStyle(l.value === typeDefault ? null : l.value)} style={{
                          padding: "6px 14px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
                          border: "1px solid " + (current === l.value ? "var(--tf-accent)" : "var(--tf-rule)"),
                          background: current === l.value ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                          color: current === l.value ? "var(--tf-accent)" : "var(--tf-fg)",
                          fontSize: 12, fontWeight: 500,
                        }} title={l.hint}>
                          {l.label}{l.value === typeDefault ? " · default" : ""}
                        </button>
                      ))}
                    </div>
                    {current !== "photoreal" && <p className="tf-muted" style={{ fontSize: 11, margin: "6px 0 0" }}>Your own products and photos stay as they are — the look styles everything around them.</p>}
                    {current === "photoreal" && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {([["phone", "Like a phone video"], ["polished", "Polished"], ["cinematic", "Cinematic"]] as const).map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setRealismRegister(v)} style={{
                            padding: "4px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", fontSize: 11,
                            border: "1px solid " + (realismRegister === v ? "var(--tf-accent)" : "var(--tf-rule)"),
                            background: realismRegister === v ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                            color: realismRegister === v ? "var(--tf-accent)" : "var(--tf-fg-muted)",
                          }}>{label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Hook style</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {HOOK_STYLES.map(h => (
                    <button key={h.value} type="button" onClick={() => setHookStyle(h.value as ContentProfile["hookStyle"])} style={{
                      padding: "6px 14px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
                      border: "1px solid " + (hookStyle === h.value ? "var(--tf-accent)" : "var(--tf-rule)"),
                      background: hookStyle === h.value ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                      color: hookStyle === h.value ? "var(--tf-accent)" : "var(--tf-fg)",
                      fontSize: 12, fontWeight: 500,
                    }}>
                      {h.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </TfCard>

          {/* ── Section 3: Audience & CTA ── */}
          <TfCard padding={24}>
            <SectionLabel number={3} title="Your audience" />
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
              <div>
                <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>
                  Channel niche 
                </label>
                <input className="tf-input" value={channelNiche} onChange={e => setChannelNiche(e.target.value)}
                  placeholder="e.g. Personal finance for millennials, Scary Reddit stories, Vegan cooking tutorials"
                  maxLength={200} style={{ width: "100%" }} />
              </div>
              <div>
                <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>
                  Target audience 
                </label>
                <textarea className="tf-input" value={targetAudience} onChange={e => setTargetAudience(e.target.value)}
                  placeholder="Who are they? What do they care about? What keeps them up at night?&#10;e.g. 25-35 year old men who want to invest but feel overwhelmed by options"
                  rows={2} maxLength={500} />
              </div>
              <div>
                <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>
                  Call to action 
                </label>
                <input className="tf-input" value={callToAction} onChange={e => setCallToAction(e.target.value)}
                  placeholder="What should the viewer DO? e.g. Subscribe, Visit my website, Buy the course, Download the app"
                  maxLength={300} style={{ width: "100%" }} />
              </div>
            </div>
          </TfCard>

          {/* ── Section 4: Product / Advanced (collapsible) ── */}
          <TfCard padding={24}>
            <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} style={{
              background: "none", border: "none", cursor: "pointer", display: "flex",
              alignItems: "center", gap: 8, width: "100%", padding: 0, color: "var(--tf-fg)", fontFamily: "inherit",
            }}>
              <SectionLabel number={4} title={isProductGoal ? "Product details" : "Advanced (optional)"} />
              <div style={{ marginLeft: "auto" }}>
                <Icon name="chevronDown" size={16} color="var(--tf-fg-muted)" />
              </div>
            </button>

            {showAdvanced && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>
                      {isProductGoal ? "Product / service name" : "Product name (optional)"}
                    </label>
                    <input className="tf-input" value={productName} onChange={e => setProductName(e.target.value)}
                      placeholder="e.g. BudgetBoss App" maxLength={200} style={{ width: "100%" }} />
                  </div>
                  <div>
                    <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>What makes you different?</label>
                    <input className="tf-input" value={differentiator} onChange={e => setDifferentiator(e.target.value)}
                      placeholder="e.g. Only app that auto-categorizes crypto transactions" maxLength={300} style={{ width: "100%" }} />
                  </div>
                </div>

                {/* Selling points */}
                <div>
                  <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Key selling points</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: sellingPoints.length ? 8 : 0 }}>
                    {sellingPoints.map((sp, i) => (
                      <span key={i} style={{
                        padding: "4px 10px", borderRadius: 6, fontSize: 12, background: "var(--tf-accent-soft)",
                        color: "var(--tf-accent)", display: "flex", alignItems: "center", gap: 6,
                      }}>
                        {sp}
                        <button type="button" aria-label="Remove this selling point" onClick={() => setSellingPoints(prev => prev.filter((_, j) => j !== i))}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--tf-accent)", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                  {sellingPoints.length < 5 && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input className="tf-input" value={newSP} onChange={e => setNewSP(e.target.value)}
                        placeholder="e.g. Saves 2 hours per week" maxLength={200}
                        onKeyDown={e => { if (e.key === "Enter" && newSP.trim()) { e.preventDefault(); setSellingPoints(prev => [...prev, newSP.trim()]); setNewSP(""); }}}
                        style={{ flex: 1 }} />
                      <Btn type="button" variant="ghost" size="sm" onClick={() => { if (newSP.trim()) { setSellingPoints(prev => [...prev, newSP.trim()]); setNewSP(""); }}}>Add</Btn>
                    </div>
                  )}
                </div>

                {/* Pain points */}
                <div>
                  <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Audience pain points</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: painPoints.length ? 8 : 0 }}>
                    {painPoints.map((pp, i) => (
                      <span key={i} style={{
                        padding: "4px 10px", borderRadius: 6, fontSize: 12, background: "var(--tf-surface-2)",
                        color: "var(--tf-danger)", display: "flex", alignItems: "center", gap: 6,
                      }}>
                        {pp}
                        <button type="button" aria-label="Remove this pain point" onClick={() => setPainPoints(prev => prev.filter((_, j) => j !== i))}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--tf-danger)", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                  {painPoints.length < 5 && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input className="tf-input" value={newPP} onChange={e => setNewPP(e.target.value)}
                        placeholder="e.g. Don't know where to start investing" maxLength={200}
                        onKeyDown={e => { if (e.key === "Enter" && newPP.trim()) { e.preventDefault(); setPainPoints(prev => [...prev, newPP.trim()]); setNewPP(""); }}}
                        style={{ flex: 1 }} />
                      <Btn type="button" variant="ghost" size="sm" onClick={() => { if (newPP.trim()) { setPainPoints(prev => [...prev, newPP.trim()]); setNewPP(""); }}}>Add</Btn>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TfCard>

          {/* ── Session 4 K2: the one-line goal (engine tenants) ── */}
          {engine && (
            <TfCard padding={24}>
              <SectionLabel number={5} title="What's the video about?" />
              <div style={{ marginTop: 12 }}>
                <input className="tf-input" value={goal} onChange={e => setGoal(e.target.value)} maxLength={300}
                  placeholder={strategyId ? "What's it about? (or leave blank to use your channel strategy's purpose)" : "e.g. Three surprising facts about the burrito"} />
                <div className="tf-muted" style={{ fontSize: 11, marginTop: 4 }}>
                  {strategyId ? "One line. Your channel strategy steers the rest." : "One line — what the video is about. That's all the engine needs."}
                </div>
              </div>
            </TfCard>
          )}

          {/* ── Section 5: Guidance ── */}
          <TfCard padding={24}>
            <SectionLabel number={engine ? 6 : 5} title="Topic guidance" />
            <div style={{ marginTop: 12 }}>
              <textarea className="tf-input" value={userGuidance} onChange={e => setUserGuidance(e.target.value)}
                placeholder="Tell the AI what this specific video should be about (optional — leave blank to let it choose)&#10;e.g. Make a video about the 3 biggest mistakes new investors make"
                rows={3} maxLength={2000} />
              <div className="tf-muted" style={{ fontSize: 11, marginTop: 4 }}>
                The content director will use your profile + this guidance to create a targeted strategy
              </div>
            </div>
          </TfCard>

          {/* ── Section 6: Voice ── */}
          <TfCard padding={24}>
            <SectionLabel number={engine ? 7 : 6} title="Narrator voice" />
            <div className="tf-muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 12 }}>
              Choose a voice for your video narration. Click the play button to hear a sample.
            </div>
            {voicesLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><TfSpinner size={20} /></div>
            ) : voices.length === 0 ? (
              <div className="tf-muted" style={{ fontSize: 12, padding: 12 }}>
                Could not load voices. The channel preset default will be used.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                {voices.map(v => {
                  const isSelected = voiceId === v.id;
                  const isPlaying = playingVoiceId === v.id;
                  return (
                    <button key={v.id} type="button" onClick={() => setVoiceId(isSelected ? "" : v.id)} style={{
                      padding: "12px 14px", textAlign: "left", position: "relative",
                      background: isSelected ? "var(--tf-accent-soft)" : "var(--tf-bg)",
                      border: "1px solid " + (isSelected ? "var(--tf-accent)" : "var(--tf-rule)"),
                      borderRadius: 8, cursor: "pointer", fontFamily: "inherit", color: "var(--tf-fg)",
                      transition: "all .15s",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{v.name}</div>
                        {previewOf(v) && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); playPreview(v); }} style={{
                            background: isPlaying ? "var(--tf-accent)" : "var(--tf-surface)",
                            border: "1px solid " + (isPlaying ? "var(--tf-accent)" : "var(--tf-rule)"),
                            borderRadius: 99, width: 28, height: 28, cursor: "pointer",
                            display: "grid", placeItems: "center", flexShrink: 0,
                            color: isPlaying ? "#fff" : "var(--tf-fg-muted)",
                            transition: "all .15s",
                          }}>
                            {isPlaying ? "■" : "▶"}
                          </button>
                        )}
                      </div>
                      <div className="tf-muted" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>
                        {v.description}
                      </div>
                      <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                        {[v.gender, v.accent, v.age.replace("_", " ")].filter(Boolean).map((tag, idx) => (
                          <span key={idx} style={{
                            padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500,
                            background: "var(--tf-surface-2)", color: "var(--tf-fg-muted)",
                          }}>{tag}</span>
                        ))}
                      </div>
                      {isSelected && (
                        <div style={{
                          position: "absolute", top: 8, right: 8, width: 18, height: 18, borderRadius: 99,
                          background: "var(--tf-accent)", color: "#fff", display: "grid", placeItems: "center",
                          fontSize: 11, fontWeight: 700,
                        }}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {!voiceId && voices.length > 0 && (
              <div className="tf-muted" style={{ fontSize: 11, marginTop: 8 }}>
                No voice selected — the channel preset default will be used
              </div>
            )}
          </TfCard>

          {/* ── Section 7: Brand Assets ── */}
          {userAssets.length > 0 && (
            <TfCard padding={24}>
              <SectionLabel number={7} title="Brand assets" />
              <div className="tf-muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                Select images the AI should reference for visual style and product appearance
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8 }}>
                {userAssets.map(a => {
                  const selected = selectedAssets.has(a.id);
                  return (
                    <button key={a.id} type="button" onClick={() => {
                      setSelectedAssets(prev => {
                        const next = new Set(prev);
                        if (next.has(a.id)) next.delete(a.id); else if (next.size < 10) next.add(a.id);
                        return next;
                      });
                    }} style={{
                      padding: 0, border: selected ? "2px solid var(--tf-accent)" : "1px solid var(--tf-rule)",
                      borderRadius: 8, cursor: "pointer", overflow: "hidden", background: "var(--tf-bg)",
                      opacity: selected ? 1 : 0.7, transition: "all .15s", position: "relative",
                    }}>
                      <img src={a.url} alt={a.filename} style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }} />
                      <div style={{ padding: "4px 6px" }}>
                        <div style={{ fontSize: 9, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--tf-fg)" }}>
                          {a.filename}
                        </div>
                        {a.analysis && (
                          <div style={{ fontSize: 8, color: "var(--tf-fg-muted)", marginTop: 1 }}>
                            {a.analysis.category}
                          </div>
                        )}
                      </div>
                      {selected && (
                        <div style={{
                          position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 99,
                          background: "var(--tf-accent)", color: "#fff", display: "grid", placeItems: "center",
                          fontSize: 11, fontWeight: 700,
                        }}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedAssets.size > 0 && (
                <div className="tf-muted" style={{ fontSize: 11, marginTop: 8 }}>
                  {selectedAssets.size} asset{selectedAssets.size !== 1 ? "s" : ""} selected — the AI will use these as visual reference
                </div>
              )}
            </TfCard>
          )}

          {/* ── Submit ── */}
          {/* (session 7 3.4) a business not ready for the type is ASKED for exactly what is missing — never a bare error */}
          {error && error.code === "NOT_READY" && Array.isArray((error.details as { missing?: unknown[] } | undefined)?.missing)
            ? <ReadinessAsk details={error.details as ReadinessDetails} onPick={(id) => { setPresetId(id); createVideo.reset(); }} />
            : error && <div style={{ padding: "10px 14px", background: "var(--tf-surface)", border: "1px solid var(--tf-danger)", borderRadius: 8, color: "var(--tf-danger)", fontSize: 13 }}>{error.message}</div>}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div className="tf-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="clock" size={13} />
              <span>{blockedWhy ? <span style={{ color: "var(--tf-accent)" }}>{blockedWhy}</span> : (engine
                ? `${ENGINE_VIDEO_COST} credits · charged only when you approve the storyboard · back if we can't make it`
                : `${CREDIT_COSTS.SCRIPT_GENERATION + CREDIT_COSTS.VIDEO_RENDER} credits · ${CREDIT_COSTS.SCRIPT_GENERATION} for the script, ${CREDIT_COSTS.VIDEO_RENDER} when it renders`)
              /* (next drop §3: the legacy form said "~5 min to ready · costs 1 credit · refunded on failure" — the legacy
                  pipeline charges the script and the render, the same 105 as the engine; no time or refund promise it can't keep) */}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {engine && <DryRunToggle on={dryRun} onChange={setDryRun} />}
              <Btn variant="ghost" onClick={() => navigate("/dashboard")}>Cancel</Btn>
              <Btn type="submit" disabled={!canSubmit} loading={createVideo.isPending} icon="sparkle" size="lg">
                Generate script
              </Btn>
            </div>
          </div>
        </form>
      )}
    </PageContainer>
  );
}

// ── Sub-components ──

function SectionLabel({ number, title }: { number: number; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 24, height: 24, borderRadius: 99, background: "var(--tf-accent)",
        color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>{number}</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
    </div>
  );
}



/** Session 7 3.4 / §7e: what this video type needs that the business doesn't have yet — and, because TubeForge has no
 *  screen to add photos or reviews to a strategy yet, NO buttons that lead nowhere: the types this business is ready for
 *  right now are offered instead ("Pick a video that needs less"). Nothing was created or charged. */
type ReadinessItem = { what: string; label: string; have: number; need: number };
type ReadinessDetails = { missing: ReadinessItem[]; quests?: ReadinessItem[]; readyTypes?: { presetId: string; name: string }[] };
function ReadinessAsk({ details, onPick }: { details: ReadinessDetails; onPick: (presetId: string) => void }) {
  return (
    <div style={{ padding: "12px 14px", background: "var(--tf-surface)", border: "1px solid var(--tf-rule)", borderRadius: 8, fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>This video needs things your business profile doesn't have yet — nothing was charged</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {details.missing.map((m) => <li key={m.what + m.need}>{m.label}{m.need > 1 ? ` (${m.have} of ${m.need})` : ""}</li>)}
      </ul>
      {(details.readyTypes ?? []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 6 }}>Pick a video that needs less:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(details.readyTypes ?? []).map((t) => (
              <button key={t.presetId} type="button" onClick={() => onPick(t.presetId)}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid var(--tf-rule)", background: "var(--tf-bg, #fff)", cursor: "pointer" }}>{t.name}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
